.topbar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  height: 100%;
  padding: 0 var(--gutter);
  min-width: 0;
}

/* Pulled left into the gutter so the glyph, rather than the button's padding,
   lines up with the breadcrumb below the bar's own left edge. */
.menuButton {
  flex: none;
  margin-left: calc(var(--space-2) * -1);
}

/* ---- Breadcrumb ---- */

.breadcrumb {
  min-width: 0;
}

.crumbs {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.crumb {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

/*
 * Every crumb but the last collapses before the last one does. The page you
 * are on is the one piece of this that has to stay readable, and truncating
 * "Knowledge" to "Kno…" to keep "Projects" whole is backwards.
 */
.crumbLink {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: var(--type-ui);
  color: var(--text-muted);
  text-decoration: none;
  border-radius: var(--radius-xs);
}

.crumbLink:hover {
  color: var(--text);
  text-decoration: underline;
  text-underline-offset: 0.2em;
}

.crumbCurrent {
  flex: none;
  font: var(--type-ui-strong);
  color: var(--text);
  white-space: nowrap;
}

.crumbSeparator {
  flex: none;
  color: var(--text-faint);
}

/* ---- Middle ---- */

/*
 * The search slot takes the slack, and the spacer stands in for it when there
 * is none, so the tools stay pinned right whether or not the page has search.
 */
.search {
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  min-width: 0;
  /* Search on a console this wide should not become a field the width of the
     window. This is the prose measure, which is also about as long a query as
     anybody types. */
  max-width: var(--content-max);
  margin: 0 auto;
}

.spacer {
  flex: 1 1 auto;
}

/* ---- Tools ---- */

.tools {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  flex: none;
}

.actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-right: var(--space-2);
}

.iconTrigger {
  display: grid;
  place-items: center;
  width: var(--control-h-sm);
  height: var(--control-h-sm);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  transition:
    color var(--dur-instant) var(--ease-out),
    background-color var(--dur-instant) var(--ease-out);
}

.iconTrigger:hover {
  color: var(--text);
  background: var(--hover);
}

.userTrigger {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: var(--control-h-sm);
  padding: 0 var(--space-2);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  transition:
    color var(--dur-instant) var(--ease-out),
    background-color var(--dur-instant) var(--ease-out);
}

.userTrigger:hover {
  color: var(--text);
  background: var(--hover);
}

.userName {
  font: var(--type-ui-strong);
  color: var(--text);
  white-space: nowrap;
}

/*
 * The role, because in a product where a `field_agent` and an `owner` see
 * genuinely different things, "which of me am I signed in as" is a question
 * worth answering without a click. Muted and small: it is context, not a label.
 */
.userRole {
  padding: 0 var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--neutral-soft);
  font: var(--type-micro);
  color: var(--text-muted);
  white-space: nowrap;
}

/*
 * In drawer mode the bar is holding a menu button, a breadcrumb and three
 * controls on a tablet. The name, the role and the search field go first: the
 * account menu still opens and still says both, one press away.
 *
 * Keyed off the shell's `data-drawer` attribute rather than off a media query.
 * The breakpoint is a number that exists once, in `preferences.ts`, and a
 * `@media (max-width: 900px)` here would be a second copy of it free to drift
 * from the first. The window width where the two disagree is a width where the
 * search field is hidden and the sidebar is still a column, or worse.
 */
[data-drawer='true'] .userName,
[data-drawer='true'] .userRole,
[data-drawer='true'] .search {
  display: none;
}
